import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPlayers, fetchGames, requestFriend, fetchFriendStatus } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { useAuth } from "../context/AuthContext";
import { DmModal } from "./DmModal";
import { HoloPlayerCard } from "./HoloPlayerCard";
import { ReportPlayerModal } from "./ReportPlayerModal";
import { ReportImageModal } from "./ReportImageModal";
import { GiftProModal } from "./GiftProModal";

export function PlayersSection({ onToast, onRequireAuth, onCompleteProfile, onOpenLeague }) {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [game, setGame] = useState("");
  const [friendStatus, setFriendStatus] = useState({});
  const [dmTarget, setDmTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportImageTarget, setReportImageTarget] = useState(null);
  const [giftTarget, setGiftTarget] = useState(null);
  const [busyFriendId, setBusyFriendId] = useState("");

  useEffect(() => { fetchGames().then(setGames); }, []);
  useEffect(() => {
    fetchPlayers(game ? { game } : {}).then(setPlayers);
  }, [game]);

  // Pluck the freshest copy of the current user out of the players feed so we
  // pick up server-side fields (teams, badges, multi-game tags) on this page.
  const selfFromFeed = user ? players.find((p) => p.user_id === user.user_id) : null;
  const selfPlayer = selfFromFeed || (user
    ? {
        user_id: user.user_id,
        name: user.name,
        avatar_id: user.avatar_id,
        pfp_url: user.pfp_url,
        game: user.game || "—",
        user_type: user.user_type || "casual",
        elo: user.elo ?? 1000,
        games_played: user.games_played || [],
        team_badges: [],
        teams: [],
        messaging_enabled: user.messaging_enabled !== false,
      }
    : null);

  useEffect(() => {
    if (!user || !players.length) return;
    Promise.all(
      players
        .filter((p) => p.user_id !== user.user_id)
        .map((p) =>
          fetchFriendStatus(p.user_id)
            .then((s) => [p.user_id, s.status])
            .catch(() => [p.user_id, "none"])
        )
    ).then((pairs) => {
      const map = {};
      for (const [uid, st] of pairs) map[uid] = st;
      setFriendStatus(map);
    });
  }, [user, players]);

  const sendFriend = async (p) => {
    if (!user) { onRequireAuth?.(); return; }
    setBusyFriendId(p.user_id);
    try {
      const r = await requestFriend(p.user_id);
      const next = r.status === "accepted" ? "friends" : "request_sent";
      setFriendStatus((prev) => ({ ...prev, [p.user_id]: next }));
      onToast?.(r.status === "accepted"
        ? `🤝 You and ${p.name} are now friends.`
        : `📨 Friend request sent to ${p.name}.`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not send friend request.");
    } finally { setBusyFriendId(""); }
  };

  const openDm = (p) => {
    if (!user) { onRequireAuth?.(); return; }
    if (p.messaging_enabled === false) {
      onToast?.(`${p.name} has DMs turned off.`);
      return;
    }
    setDmTarget(p);
  };

  return (
    <section
      id="players"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z border-t border-[#14b8a6]/20"
    >
      <div className="flex justify-between items-end flex-wrap gap-6 mb-10">
        <div>
          <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
            OPERATORS ONLINE
          </span>
          <h2 className="text-4xl md:text-5xl font-display neon-text mt-5">PLAYERS</h2>
          <p className="text-lg text-[#a3a3ac] mt-3">Top operators ranked by MMR.</p>
        </div>
        <select
          value={game}
          onChange={(e) => setGame(e.target.value)}
          data-testid="players-game-filter"
          className="bg-black/50 border border-[#14b8a6]/30 rounded-3xl px-5 py-3 text-white"
        >
          <option value="">ALL GAMES</option>
          {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
        </select>
      </div>

      {selfPlayer && (
        <div className="mb-8" data-testid="players-self-card">
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] mb-3">
            <i className="fas fa-user-astronaut mr-2" /> YOUR OPERATOR
          </p>
          <div className="glass rounded-3xl p-5 flex flex-col md:flex-row items-start md:items-center gap-5 border border-[#14b8a6]/40">
            <AvatarChip avatarId={selfPlayer.avatar_id} pfpUrl={selfPlayer.pfp_url} size={72} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-2xl text-white truncate">{selfPlayer.name}</h3>
                <span className="text-[10px] tracking-widest px-2 py-0.5 rounded-md bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-[#14b8a6]">YOU</span>
                <span className="text-[10px] tracking-widest px-2 py-0.5 rounded-md bg-black/40 border border-[#14b8a6]/30 text-[#a3a3ac]">
                  {(selfPlayer.user_type || "casual").toUpperCase()}
                </span>
              </div>
              <p className="text-[11px] text-[#a3a3ac] mt-1">
                {selfPlayer.game}{selfPlayer.user_type === "pro" ? " · PRO" : ""} · MMR {selfPlayer.elo}
              </p>
              {selfPlayer.games_played?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid="players-self-games">
                  <span className="text-[9px] tracking-widest text-[#14b8a6] mr-1">PLAYS</span>
                  {selfPlayer.games_played.slice(0, 6).map((g) => (
                    <span key={g} className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/40 border border-[#14b8a6]/30 text-[#14b8a6]">
                      {g}
                    </span>
                  ))}
                </div>
              )}
              {selfPlayer.teams?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid="players-self-teams">
                  {selfPlayer.teams.slice(0, 4).map((t) => (
                    <span
                      key={t.team_id}
                      className={`inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-0.5 rounded-lg bg-black/40 border ${
                        t.is_manager ? "border-[#14b8a6]/50 text-[#14b8a6]" : "border-[#14b8a6]/30 text-[#a3a3ac]"
                      }`}
                    >
                      {t.is_manager && <i className="fas fa-crown text-[9px]" />}
                      <i className="fas fa-shield-halved text-[9px]" />
                      <span className="truncate max-w-[140px]">{t.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => onCompleteProfile?.()}
              data-testid="players-self-edit-btn"
              className="btn-neon rounded-2xl px-5 py-3 text-xs tracking-widest"
            >
              <i className="fas fa-pen-to-square mr-2" /> VIEW / EDIT PROFILE
            </button>
          </div>
        </div>
      )}

      {players.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center text-white/60">
          No operators yet. Be the first.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="players-grid">
          {players.map((p) => (
            <HoloPlayerCard
              key={p.user_id}
              p={p}
              isSelf={user?.user_id === p.user_id}
              friendStatus={friendStatus[p.user_id] || "none"}
              busyFriend={busyFriendId === p.user_id}
              onSendFriend={() => sendFriend(p)}
              onOpenDm={() => openDm(p)}
              onOpenLeague={onOpenLeague}
              onReport={(target) => user ? setReportTarget(target) : onRequireAuth?.()}
              onReportImage={(target, kind) => user ? setReportImageTarget({ ...target, _kind: kind }) : onRequireAuth?.()}
              onGiftPro={(target) => user ? setGiftTarget(target) : onRequireAuth?.()}
            />
          ))}
        </div>
      )}

      {dmTarget && (
        <DmModal
          targetUser={dmTarget}
          onClose={() => setDmTarget(null)}
          onToast={onToast}
        />
      )}
      <ReportPlayerModal
        open={!!reportTarget}
        player={reportTarget}
        onClose={() => setReportTarget(null)}
        onToast={onToast}
      />
      <ReportImageModal
        open={!!reportImageTarget}
        target={reportImageTarget}
        imageKind={reportImageTarget?._kind}
        onClose={() => setReportImageTarget(null)}
        onToast={onToast}
      />
      <GiftProModal
        open={!!giftTarget}
        player={giftTarget}
        onClose={() => setGiftTarget(null)}
        onToast={onToast}
      />
    </section>
  );
}

export default PlayersSection;
